import { TourStep } from 'components/tour'

// Opens Hub settings from the ⋯ beside the hub's name in the sidebar.
const OPEN_HUB_SETTINGS: TourStep[] = [
  {
    target: "[data-tour='hub-options']",
    content: 'Click ⋯ next to the hub name in the sidebar.',
    requiresInteraction: true
  },
  {
    target: "[data-tour='hub-settings-link']",
    content: "Select 'Hub settings'.",
    requiresInteraction: true
  }
]

// ─── Reusable step fragments ───────────────────────────────────────────────────
// Each fragment is a mini-tour that can run standalone or be composed into larger tours.

const STEPS = {
  // ── Navigation ──────────────────────────────────────────────────────────────

  navigate_to_hub: [
    {
      target: "[data-tour='hubs-link']",
      content: 'Click All hubs in the sidebar to see your hub list.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='hub-list']",
      content: 'Click on a hub to enter it.',
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Hub ─────────────────────────────────────────────────────────────────────

  create_hub: [
    {
      target: "[data-tour='new-hub']",
      content: "Click 'New Hub' to create a new hub.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='hub-name-input']",
      content: 'Enter a name for your new hub.'
    },
    {
      target: "[data-tour='create-hub-submit']",
      content: "Click 'Create Hub' to finish.",
      requiresInteraction: true
    }
  ] as TourStep[],

  invite_members: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='invite-members']",
      content: "Click 'Invite Members' to send invitations to new users.",
      requiresInteraction: true
    }
  ] as TourStep[],

  manage_hub_settings: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-members']",
      content: 'Open the Members tab to manage who has access to this hub.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='invite-members']",
      content: "Click 'Invite Members' to add new collaborators.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='nav-section-webhooks']",
      content: 'Open the Webhooks tab to connect this hub to external systems.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-webhook']",
      content: "Click 'Add Webhook' to configure event notifications.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='nav-section-secrets']",
      content: 'Open the Secrets tab to store credentials safely for automations.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-secret']",
      content: "Click 'Add Secret' to add a protected value for scripts and workflows.",
      requiresInteraction: true
    }
  ] as TourStep[],

  manage_information_settings: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-information']",
      content: 'Open the Information tab to manage hub-wide configuration.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='hub-name-input']",
      content: 'Update your hub name and general settings from this section.'
    },
    {
      target: "[data-tour='default-document-selector']",
      content: 'Use this selector to choose the default document shown to members.'
    }
  ] as TourStep[],

  manage_styling_settings: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-styling']",
      content: 'Open the Styling tab to customise branding and export styling.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='change-word-template']",
      content: 'Add or change a Word template used for document exports.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='bulk-apply-column-colours']",
      content: 'Give every column already in the hub the default header colours.',
      requiresInteraction: true
    }
  ] as TourStep[],

  manage_bulk_updates: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-updates']",
      content: 'Open Bulk updates to change many documents and tables at once.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='bulk-update-text-apply']",
      content: 'Replace text across every document once the text to find and its replacement are entered.'
    },
    {
      target: "[data-tour='bulk-update-permissions-apply']",
      content: 'Below it, replace the permissions of chosen documents, tables or views in one go.'
    }
  ] as TourStep[],

  manage_invites: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-members']",
      content: 'Open Members: pending invitations are listed with the members.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='members-show-tabs']",
      content: 'Choose Invited to show only pending invites. Resend, edit or delete one from its ⋯ menu.'
    }
  ] as TourStep[],

  restore_deleted_resources: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-archived']",
      content: 'Open Archived files to find archived documents and tables.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='deleted-resources-table']",
      content: 'Everything archived in this hub is listed here, to search, sort and restore.'
    },
    {
      target: "[data-tour='restore-deleted-resource']",
      content: "Click 'Restore' to put one back where it was in the hub.",
      requiresInteraction: true
    }
  ] as TourStep[],

  review_audit_logs: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-audit']",
      content: 'Open Audit to inspect activity history across your hub.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='audit-get-more-logs']",
      content: "Load additional historical records by clicking 'Get More Logs'.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Table creation ──────────────────────────────────────────────────────────

  create_table: [
    {
      target: "[data-tour='add-resource']",
      content: 'Click the + button to open the resource menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='create-new-table']",
      content: "Select 'New table' to open the table creation dialog.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='table-name-input']",
      content: 'Enter a name for your new table.'
    },
    {
      target: "[data-tour='create-table-submit']",
      content: "Click 'Create New Table' to finish.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Document creation ───────────────────────────────────────────────────────

  create_document: [
    {
      target: "[data-tour='add-resource']",
      content: 'Click the + button to open the resource menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='create-new-document']",
      content: "Select 'New document' to open the document creation dialog.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='document-name-input']",
      content: 'Enter a name for your new document.'
    },
    {
      target: "[data-tour='create-document-submit']",
      content: "Click 'Create New Document' to finish.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Column editing ──────────────────────────────────────────────────────────

  edit_column_type: (typeName?: string): TourStep[] => [
    {
      target: "[data-tour='column-options-button']",
      content: 'Click the ⋮ on a column header to open the column menu (it shows when you point at the column).',
      requiresInteraction: true
    },
    {
      target: "[data-tour='edit-column-button']",
      content: "Click 'Edit column' to open the column settings.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='column-type-selector']",
      content: 'Click this dropdown to open the list of column types.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='column-type-selector']",
      content: typeName
        ? `Select '${typeName}' from the dropdown.`
        : 'Select a type from the dropdown (e.g. Number, Select, Date).',
      requiresInteraction: true
    },
    {
      target: "[data-tour='confirm-change-column-kind']",
      content: "Click 'Change Kind' to confirm the column type change.",
      requiresInteraction: true,
      dismissModalOnAdvance: true
    }
  ],

  set_default_value: [
    {
      target: "[data-tour='column-options-button']",
      content: 'Click the ⋮ on a column header to open the column menu (it shows when you point at the column).',
      requiresInteraction: true
    },
    {
      target: "[data-tour='edit-column-button']",
      content: "Click 'Edit column' to open the column settings.",
      requiresInteraction: true
    },
    {
      // A column with a default already shows its field; one without has Add default value in its place.
      target: "[data-tour='column-add-default-value'], [data-tour='column-default-value']",
      content:
        "Click 'Add default value' to show the default value field. If the column already has a default, click its Default value field.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='column-default-value']",
      content:
        'Type a default value here. New rows will automatically use this value for the column. Click Next when done.'
    }
  ] as TourStep[],

  change_column_header_colour: [
    {
      target: "[data-tour='column-options-button']",
      content: 'Click the ⋮ on a column header to open the column menu (it shows when you point at the column).',
      requiresInteraction: true
    },
    {
      target: "[data-tour='column-header-formatting']",
      content: "Click 'Header formatting' to open the header's colours.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='column-header-background']",
      content: 'Click Background and pick a colour for the column header. Click Next when done.'
    }
  ] as TourStep[],

  assign_user_tags: [
    ...OPEN_HUB_SETTINGS,
    {
      target: "[data-tour='nav-section-members']",
      content: 'Open the Members tab to see hub members and their tag assignments.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='member-tag-select']",
      content:
        'Use this dropdown to assign tag values to a user. Tags come from Tag columns in your tables. Click Next when done.'
    }
  ] as TourStep[],

  filter_by_tag_values: [
    {
      target: "[data-tour='table-filter-button']",
      content: 'Click the filter icon to open the filter menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-filter']",
      content: 'Select a column to filter on.'
    },
    {
      target: "[data-tour='filter-value-input']",
      content:
        "Type {{user.tag_values}} as the filter value. This dynamically resolves to the current user's assigned tag values, so each user sees only their own rows."
    }
  ] as TourStep[],

  add_column: [
    {
      target: "[data-tour='add-column']",
      content: "Click the '+' at the end of the header row to add a new column.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Row editing ─────────────────────────────────────────────────────────────

  add_rows: [
    {
      target: "[data-tour='add-row']",
      content: 'Click here to add a new row to your table.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='table-first-cell']",
      content: 'Click on a cell and type a value. Click Next when done.'
    }
  ] as TourStep[],

  // ── View management ─────────────────────────────────────────────────────────

  create_view: [
    {
      target: "[data-tour='view-selector']",
      content: 'Click here to open the views menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='new-spreadsheet-view']",
      content: "Click 'New Spreadsheet View' to create a new view. You can also create Form or Chart views.",
      requiresInteraction: true
    }
  ] as TourStep[],

  save_view: [
    {
      target: "[data-tour='table-save-button']",
      content: 'Click the save icon to save your view configuration.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='save-current-view']",
      content: "Click 'Save This View' to save your filters, sorts, and groups.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Toolbar features ────────────────────────────────────────────────────────

  add_filter: [
    {
      target: "[data-tour='table-filter-button']",
      content: 'Click the filter icon to open the filter menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-filter']",
      content: 'Select a column from the dropdown to add a filter.'
    }
  ] as TourStep[],

  add_sort: [
    {
      target: "[data-tour='table-sort-button']",
      content: 'Click the sort icon to open the sort menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-sort']",
      content: 'Select a column from the dropdown to add a sort.'
    }
  ] as TourStep[],

  add_group: [
    {
      target: "[data-tour='table-group-button']",
      content: 'Click the group icon to open the group menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-group']",
      content: 'Select a column from the dropdown to group your rows by.'
    }
  ] as TourStep[],

  hide_columns: [
    {
      target: "[data-tour='table-hide-button']",
      content: 'Click the hide icon to toggle column visibility.',
      requiresInteraction: true
    }
  ] as TourStep[],

  colour_rows: [
    {
      target: "[data-tour='table-colour-button']",
      content: 'Click the colour icon to open the colour rules menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='add-colour']",
      content: 'Select a column to base your colour rule on.'
    }
  ] as TourStep[],

  table_permissions: [
    {
      target: "[data-tour='table-permissions-button']",
      content: 'Click the permissions icon to manage who can view and edit this table.',
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Data operations ─────────────────────────────────────────────────────────

  import_data: [
    {
      target: "[data-tour='table-import-button']",
      content: 'Click the import button to open the import menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='import-from-file']",
      content: "Select 'Import From File (CSV / Excel)' to upload your data.",
      requiresInteraction: true
    }
  ] as TourStep[],

  export_table: [
    {
      target: "[data-tour='table-export-button']",
      content: 'Click the export icon to open export options.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='export-csv']",
      content: "Click 'CSV' to download your table data as a CSV file.",
      requiresInteraction: true
    }
  ] as TourStep[],

  search_table: [
    {
      target: "[data-tour='table-search-button']",
      content: 'Click the search icon to open the search bar.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='search-input']",
      content: 'Type your search term to find matching cells. Use the arrows to navigate between results.'
    }
  ] as TourStep[],

  lookup_data: [
    {
      target: "[data-tour='table-joins-button']",
      content: 'Click the lookup icon to create relationships between tables.',
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Document operations ─────────────────────────────────────────────────────

  edit_document: [
    {
      target: "[data-tour='document-edit-toggle']",
      content: 'Click Edit to switch to edit mode, and Done to go back to viewing.',
      requiresInteraction: true
    }
  ] as TourStep[],

  export_document: [
    {
      target: "[data-tour='document-export-button']",
      content: 'Click the export icon to download this document as a Word file.',
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Navigation (integrations) ───────────────────────────────────────────────

  navigate_to_integrations: [
    {
      target: "[data-tour='user-profile-button']",
      content: 'Click your profile to open the user menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='user-menu-integrations']",
      content: "Select 'Integrations' to manage API keys and connected accounts.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Integrations ───────────────────────────────────────────────────────────

  manage_api_keys: [
    {
      target: "[data-tour='api-keys-section']",
      content: 'This section lets you create and manage API keys for programmatic access.'
    },
    {
      target: "[data-tour='new-api-key']",
      content: "Click 'New API Key' to create a key for scripts or external tools.",
      requiresInteraction: true
    }
  ] as TourStep[],

  connect_integrations: [
    {
      target: "[data-tour='connected-accounts-section']",
      content: 'Connect external systems here to import data into Morta.'
    },
    {
      target: "[data-tour='integration-connect-account']",
      content: "Click 'Connect Account' for any provider you want to link.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Manual sync ────────────────────────────────────────────────────────────

  manual_sync: [
    {
      target: "[data-tour='table-import-button']",
      content: 'Click the import button to open the import menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='sync-settings-header']",
      content: 'This section shows your external import settings for the current integration.'
    },
    {
      target: "[data-tour='sync-last-successful']",
      content: 'Here you can see when the last successful import occurred.'
    },
    {
      target: "[data-tour='sync-auto-refresh']",
      content: 'Use this dropdown to schedule automatic imports (e.g. every day at midnight).'
    },
    {
      target: "[data-tour='sync-now-button']",
      content: "Click 'Import Now' to trigger a manual import. You will receive an email when it completes.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Templates ───────────────────────────────────────────────────────────────

  use_template: [
    {
      target: "[data-tour='add-resource']",
      content: 'Click the + button to open the resource menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='create-from-template']",
      content: "Select 'New from template' to browse available templates.",
      requiresInteraction: true
    }
  ] as TourStep[],

  // ── Sidebar folders ─────────────────────────────────────────────────────────

  organise_sidebar_folders: [
    {
      target: "[data-tour='add-resource']",
      content: 'Click the + button to open the resource menu.',
      requiresInteraction: true
    },
    {
      target: "[data-tour='create-new-folder']",
      content: "Select 'New folder'. Type a name for it and press Enter to save.",
      requiresInteraction: true
    },
    {
      target: "[data-tour='sidebar-tree']",
      content:
        'Drag documents and tables onto a folder to move them in, or between rows to reorder them. Hold Cmd/Ctrl or Shift and click to select several first.'
    },
    {
      target: "[data-tour='sidebar-tree']",
      content:
        "Hover over a folder and click its + to add a subfolder, document or table inside it. Right-click a folder, or click its ⋯, to rename or delete it, or do the same on a document or table to rename it, choose 'Move to…' or archive it. Double-click a folder's name, or press F2 on a focused row, to rename it."
    }
  ] as TourStep[]
}

// ─── Tour definitions ──────────────────────────────────────────────────────────
// Composed from fragments. Each tour can be a mini-tour or a full walkthrough.

interface Tour {
  id: string
  name: string
  steps: TourStep[]
  /** What it shows is for hub Owners only (folder management), so no one else is offered it. */
  hubOwnersOnly?: boolean
}

/** Whether a viewer may be walked through `tour`: an Owner-only tour needs a hub Owner. */
export const canStartTour = (tour: Tour, isHubOwner: boolean): boolean => !tour.hubOwnersOnly || isHubOwner

const TOURS: Record<string, Tour> = {
  // ── Table tours ─────────────────────────────────────────────────────────────

  new_table: {
    id: 'new_table',
    name: 'Create a new table',
    steps: [
      ...STEPS.navigate_to_hub,
      ...STEPS.create_table,
      ...STEPS.edit_column_type(),
      ...STEPS.add_rows,
      ...STEPS.table_permissions,
      ...STEPS.create_view,
      ...STEPS.add_filter,
      ...STEPS.add_sort,
      ...STEPS.add_group,
      ...STEPS.save_view
    ]
  },
  add_column: {
    id: 'add_column',
    name: 'Add a column to a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.add_column]
  },
  add_rows: {
    id: 'add_rows',
    name: 'Add rows to a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.add_rows]
  },
  edit_column_type: {
    id: 'edit_column_type',
    name: 'Edit a column type',
    steps: [...STEPS.navigate_to_hub, ...STEPS.edit_column_type()]
  },
  save_view: {
    id: 'save_view',
    name: 'Save a view',
    steps: [...STEPS.navigate_to_hub, ...STEPS.save_view]
  },
  filter_table: {
    id: 'filter_table',
    name: 'Filter a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.add_filter]
  },
  sort_table: {
    id: 'sort_table',
    name: 'Sort a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.add_sort]
  },
  group_table: {
    id: 'group_table',
    name: 'Group rows in a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.add_group]
  },
  hide_columns: {
    id: 'hide_columns',
    name: 'Hide columns in a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.hide_columns]
  },
  colour_rows: {
    id: 'colour_rows',
    name: 'Colour rows in a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.colour_rows]
  },
  import_data: {
    id: 'import_data',
    name: 'Import data into a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.import_data]
  },
  export_table: {
    id: 'export_table',
    name: 'Export table data',
    steps: [...STEPS.navigate_to_hub, ...STEPS.export_table]
  },
  search_table: {
    id: 'search_table',
    name: 'Search within a table',
    steps: [...STEPS.navigate_to_hub, ...STEPS.search_table]
  },
  table_permissions: {
    id: 'table_permissions',
    name: 'Manage table permissions',
    steps: [...STEPS.navigate_to_hub, ...STEPS.table_permissions]
  },
  lookup_data: {
    id: 'lookup_data',
    name: 'Look up data from other tables',
    steps: [...STEPS.navigate_to_hub, ...STEPS.lookup_data]
  },
  change_column_header_colour: {
    id: 'change_column_header_colour',
    name: 'Change a column header colour',
    steps: [...STEPS.navigate_to_hub, ...STEPS.change_column_header_colour]
  },
  set_default_value: {
    id: 'set_default_value',
    name: 'Set a default value for a column',
    steps: [...STEPS.navigate_to_hub, ...STEPS.set_default_value]
  },
  assign_user_tags: {
    id: 'assign_user_tags',
    name: 'Assign tags to users',
    steps: [
      ...STEPS.navigate_to_hub,
      ...STEPS.create_table,
      ...STEPS.edit_column_type('Tag'),
      ...STEPS.assign_user_tags
    ]
  },
  filter_by_tag_values: {
    id: 'filter_by_tag_values',
    name: 'Filter a table by user tag values',
    steps: [
      ...STEPS.navigate_to_hub,
      ...STEPS.create_table,
      ...STEPS.edit_column_type('Tag'),
      ...STEPS.assign_user_tags,
      {
        target: "[data-tour='sidebar-folders']",
        content: 'Now click on your table in the sidebar to go back to it.',
        requiresInteraction: true
      },
      ...STEPS.filter_by_tag_values
    ]
  },

  // ── View tours ──────────────────────────────────────────────────────────────

  new_view: {
    id: 'new_view',
    name: 'Create a new view',
    steps: [...STEPS.navigate_to_hub, ...STEPS.create_view]
  },

  // ── Document tours ──────────────────────────────────────────────────────────

  new_document: {
    id: 'new_document',
    name: 'Create a new document',
    steps: [...STEPS.navigate_to_hub, ...STEPS.create_document]
  },
  edit_document: {
    id: 'edit_document',
    name: 'Edit a document',
    steps: [...STEPS.navigate_to_hub, ...STEPS.edit_document]
  },
  export_document: {
    id: 'export_document',
    name: 'Export a document to Word',
    steps: [...STEPS.navigate_to_hub, ...STEPS.export_document]
  },

  // ── Hub tours ───────────────────────────────────────────────────────────────

  new_hub: {
    id: 'new_hub',
    name: 'Create a new hub',
    steps: [...STEPS.create_hub]
  },
  invite_members: {
    id: 'invite_members',
    name: 'Invite members to a hub',
    steps: [...STEPS.navigate_to_hub, ...STEPS.invite_members]
  },
  manage_hub_settings: {
    id: 'manage_hub_settings',
    name: 'Manage hub settings',
    steps: [...STEPS.navigate_to_hub, ...STEPS.manage_hub_settings]
  },
  manage_information_settings: {
    id: 'manage_information_settings',
    name: 'Manage hub information settings',
    steps: [...STEPS.navigate_to_hub, ...STEPS.manage_information_settings]
  },
  manage_styling_settings: {
    id: 'manage_styling_settings',
    name: 'Manage hub styling settings',
    steps: [...STEPS.navigate_to_hub, ...STEPS.manage_styling_settings]
  },
  manage_bulk_updates: {
    id: 'manage_bulk_updates',
    name: 'Run hub bulk updates',
    steps: [...STEPS.navigate_to_hub, ...STEPS.manage_bulk_updates]
  },
  manage_invites: {
    id: 'manage_invites',
    name: 'Manage hub invites',
    steps: [...STEPS.navigate_to_hub, ...STEPS.manage_invites]
  },
  restore_deleted_resources: {
    id: 'restore_deleted_resources',
    name: 'Restore archived documents and tables',
    steps: [...STEPS.navigate_to_hub, ...STEPS.restore_deleted_resources]
  },
  review_audit_logs: {
    id: 'review_audit_logs',
    name: 'Review audit logs',
    steps: [...STEPS.navigate_to_hub, ...STEPS.review_audit_logs]
  },
  use_template: {
    id: 'use_template',
    name: 'Create from a template',
    steps: [...STEPS.navigate_to_hub, ...STEPS.use_template]
  },
  manage_api_keys: {
    id: 'manage_api_keys',
    name: 'Create and manage API keys',
    steps: [...STEPS.navigate_to_integrations, ...STEPS.manage_api_keys]
  },
  connect_integrations: {
    id: 'connect_integrations',
    name: 'Connect an external account',
    steps: [...STEPS.navigate_to_integrations, ...STEPS.connect_integrations]
  },

  // ── Integration tours ──────────────────────────────────────────────────────

  manual_sync: {
    id: 'manual_sync',
    name: 'Run a manual import for an integration',
    steps: [...STEPS.manual_sync]
  },

  // ── Organise sidebar ────────────────────────────────────────────────────────

  organise_sidebar_folders: {
    id: 'organise_sidebar_folders',
    name: 'Organise sidebar into folders',
    hubOwnersOnly: true,
    steps: [...STEPS.navigate_to_hub, ...STEPS.organise_sidebar_folders]
  }
}

export { STEPS }
export default TOURS
